import React, { useState, useRef, useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, ChevronLeft, ChevronDown } from "lucide-react";
import { LOGO_URL, SOLUTIONS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import NotificacionesBell from "./NotificacionesBell";

// F1.1 · APPLICATION MODE — ConstruLink actúa como contenedor. La app especializada
// (COREX y futuros programas) ocupa casi toda la pantalla; ConstruLink conserva solo
// una barra superior compacta. Layout oficial reutilizable para todos los programas.
export default function ApplicationModeLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appsOpen, setAppsOpen] = useState(false);
  const appsRef = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (appsRef.current && !appsRef.current.contains(e.target)) setAppsOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "CL";

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-900" data-testid="application-mode">
      <header
        className="h-14 shrink-0 bg-white border-b border-slate-200 px-3 sm:px-5 flex items-center justify-between gap-3 z-30"
        data-testid="appmode-topbar"
      >
        {/* Logo + retorno al ecosistema */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate("/software")}
            data-testid="appmode-back"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors shrink-0"
          >
            <ChevronLeft size={15} /> ConstruLink
          </button>
          <Link to="/" className="flex items-center gap-2 min-w-0 shrink" data-testid="appmode-logo">
            <img src={LOGO_URL} alt="ConstruLink" className="h-8 w-auto max-w-[150px] object-contain" />
          </Link>
        </div>

        {/* Acciones compactas del ecosistema */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
          <div className="relative" ref={appsRef}>
            <button
              onClick={() => setAppsOpen((v) => !v)}
              data-testid="appmode-apps-button"
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-slate-700"
              aria-label="Aplicaciones del ecosistema"
            >
              <LayoutGrid size={17} />
              <span className="text-xs font-semibold hidden sm:inline">Apps</span>
            </button>
            {appsOpen && (
              <div className="absolute right-0 mt-2 w-[320px] bg-white rounded-2xl shadow-lifted border border-slate-100 p-4 z-40" data-testid="appmode-apps-menu">
                <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-3 px-1">
                  Ecosistema ConstruLink
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {SOLUTIONS.map((s) => {
                    const Icon = s.Icon;
                    return (
                      <Link
                        key={s.id}
                        to={s.path}
                        onClick={() => setAppsOpen(false)}
                        className="group flex flex-col items-center justify-center gap-2 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                        data-testid={`appmode-apps-${s.id}`}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.tint, color: s.color }}>
                          <Icon size={20} />
                        </div>
                        <span className="text-[11px] font-semibold text-slate-700 text-center leading-tight">
                          {s.title.split(" ")[0]}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <NotificacionesBell />

          <Link
            to="/mi-perfil"
            data-testid="appmode-profile"
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-blue to-brand-navy text-white text-xs font-bold flex items-center justify-center">
              {initials}
            </div>
            <span className="text-sm font-semibold text-slate-800 leading-tight hidden md:block max-w-[140px] truncate">
              {user?.name || "Usuario"}
            </span>
            <ChevronDown size={15} className="text-slate-500 hidden sm:block" />
          </Link>
        </div>
      </header>

      <main className="flex-1 min-h-0" data-testid="appmode-main">
        <Outlet />
      </main>
    </div>
  );
}
